/**
 * PHY config assembly for the Config sheet — the pure half, kept out of the DOM so the
 * command string (a field-critical value) is unit-tested. The firmware validates what it
 * receives (REQ-CON-9); this only builds a well-formed `cfg` line from the picker state.
 *
 * The firmware derives the band from the frequency, so `band` here is UI sugar: it seeds a
 * default frequency and the per-band power clamp (PROTOCOL.md §2.2). Every key is sent, so
 * APPLY fully defines the point rather than depending on the board's prior state.
 */
export type Band = '868' | '2.4G';

export type CfgState = {
  freq: number;
  sf: number;
  bw: number;
  cr: string; // '4/5' … '4/8'
  pwr: number;
  sync: number;
};

export const BANDS: Record<Band, { freq: number; pwrMin: number; pwrMax: number }> = {
  '868': { freq: 868100000, pwrMin: -9, pwrMax: 22 },
  '2.4G': { freq: 2450000000, pwrMin: -19, pwrMax: 12 },
};

/** Accepted firmware picker values (PROTOCOL.md §2.2). */
export const SF_VALUES = [5, 6, 7, 8, 9, 10, 11, 12] as const;
export const BW_VALUES = [31, 41, 62, 83, 101, 125, 203, 250, 406, 500, 812, 1000] as const;
export const CR_VALUES = ['4/5', '4/6', '4/7', '4/8'] as const;
export const SYNC_VALUES = [
  { label: 'private 0x12', value: 0x12 },
  { label: 'public 0x34', value: 0x34 },
] as const;

/** The firmware's band-from-frequency rule. */
export function bandOf(freq: number): Band {
  return freq >= 2_400_000_000 ? '2.4G' : '868';
}

/** Clamp a power to the band's accepted range. */
export function clampPwr(pwr: number, band: Band): number {
  const b = BANDS[band];
  return Math.max(b.pwrMin, Math.min(b.pwrMax, pwr));
}

/** Assemble the full `cfg` command from the picker state. */
export function buildCfg(s: CfgState): string {
  const sync = '0x' + (s.sync & 0xff).toString(16).padStart(2, '0');
  return `cfg freq=${s.freq} sf=${s.sf} bw=${s.bw} cr=${s.cr} pwr=${s.pwr} sync=${sync}`;
}

/** The `cfg tofdelay=` command from the ranging field (PROTOCOL.md §2.2). */
export function setDelayCommand(value: string): string {
  return `cfg tofdelay=${value.trim()}`;
}

/**
 * The `cfg pwrmax=` command — a **ceiling** on `pwr`, not a power. The node transmits at
 * `min(pwr, pwrmax)`, then band-clamps (PROTOCOL.md §2.2, range −19…+22).
 *
 * Sent by its own button and deliberately absent from {@link buildCfg} (07 §9.1): a ceiling is a
 * session-level safety setting, not a coordinate of the matrix point. If APPLY carried it, one tap
 * could silently widen a limit the operator set — and the firmware's own rule is that `pwrmax` is
 * stored as typed and never changed on its own, including by a later `cfg freq=`. The sheet
 * already treats `tofdelay` this way, which is the shape this follows.
 */
export function setPwrmaxCommand(value: string): string {
  return `cfg pwrmax=${value.trim()}`;
}

/**
 * Clamp a `pwr=` token **in place**, leaving every other byte of the line untouched.
 *
 * In place matters. Rebuilding through {@link buildCfg} would drop the laptop's `hold=` and
 * `pwrmax=` and add keys it never sent; the page forwards the laptop's own line, changing exactly
 * the bytes of one value. That is also what makes the `sent <id> <line>` note honest.
 *
 * @param freq the frequency the line will leave the board on — the line's own `freq=` if it
 *        carries one, otherwise the page's current state. The band decides the ceiling, so
 *        `cfg freq=2450000000 pwr=20` must clamp against 2.4 GHz and not against 868.
 * @returns the line unchanged when there is no `pwr=` token, when its value is not a number, or
 *          when it is already in band.
 */
export function clampPwrInLine(line: string, freq: number): string {
  // The leading boundary is what keeps `pwrmax=` out of this. The firmware guards the same prefix
  // hazard the same way — proto.cpp:147, "whole-string match, so there is no prefix hazard
  // between `pwr` and `pwrmax`" — and getting it wrong here would silently rewrite a ceiling.
  const m = /(^|\s)pwr=(\S*)/.exec(line);
  if (!m) return line;
  const raw = m[2] ?? '';
  const n = Number(raw);
  if (raw === '' || !Number.isFinite(n)) return line; // the board answers `err cfg pwr`; not ours
  const safe = clampPwr(n, bandOf(freq));
  if (safe === n) return line; // numeric compare, so `pwr=+5` is left as the operator wrote it
  const at = m.index + (m[1] ?? '').length;
  return `${line.slice(0, at)}pwr=${safe}${line.slice(at + 'pwr='.length + raw.length)}`;
}

/**
 * Update the picker state from a `cfg` line — a generic `key=value` scan, the same shape
 * `decode.ts` already uses for display (REQ-CON-9a).
 *
 * It reads **only** the six keys {@link CfgState} models and ignores every other token, so a line
 * carrying `hold=`, `pwrmax=` or `tofdelay=` merges cleanly and those keys simply pass by. It
 * **cannot refuse a command** — it has no reject path, which is precisely why it is not a second
 * copy of the grammar (02 §7) and does not violate REQ-CON-9.
 *
 * What it deliberately does *not* do is validate ranges. `cfg sf=99` leaves 99 in the picker even
 * though the board answers `err cfg sf` and applies nothing, so the two disagree until the next
 * `cfg?`. That is the accepted cost of having exactly one grammar, and the board's `err` line
 * travels back over the same channel and into the log. A range check here would be a second
 * authority, and a stricter one would refuse legitimate commands two kilometres away.
 *
 * A malformed *value* is skipped rather than stored: `sf=abc` would otherwise put `NaN` into the
 * state and send `sf=NaN` on the next APPLY, which is a fault the page introduced rather than one
 * it forwarded.
 */
export function mergeCfgLine(prev: CfgState, line: string): CfgState {
  const next: CfgState = { ...prev };
  for (const tok of line.split(/\s+/)) {
    const eq = tok.indexOf('=');
    if (eq <= 0) continue; // no `=`, or a leading `=`; also skips the `cfg` verb and `pwr?` asks
    const key = tok.slice(0, eq);
    const val = tok.slice(eq + 1);
    if (val === '') continue;
    // `Number` reads both spellings the firmware accepts for sync: 0x12 and 18.
    const n = Number(val);
    const num = Number.isFinite(n);
    if (key === 'cr') next.cr = val;
    else if (num && key === 'freq') next.freq = n;
    else if (num && key === 'sf') next.sf = n;
    else if (num && key === 'bw') next.bw = n;
    else if (num && key === 'pwr') next.pwr = n;
    else if (num && key === 'sync') next.sync = n;
  }
  return next;
}

/**
 * Cautions for a band-questionable config — the firmware still decides (a warned combo is
 * often a deliberate off-matrix probe, e.g. the BW125-at-2.4 GHz contradiction point the
 * campaign names). These flag, they do not block. Rules are the ones the project documents;
 * add more only with a cited source.
 */
export function cfgWarnings(cfg: CfgState): string[] {
  const w: string[] = [];
  const band = bandOf(cfg.freq);
  if (band === '2.4G' && cfg.bw < 203) {
    w.push(`BW ${cfg.bw} kHz is below the 2.4 GHz minimum of 203 (datasheet) — the radio may answer err cfg`);
  }
  if (band === '868' && cfg.sf < 7) {
    w.push(`SF${cfg.sf} is a 2.4 GHz mode — sub-GHz LoRa uses SF7–12`);
  }
  return w;
}
